import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import { reportCaughtError } from 'magical-trails-react';
import loadOpenCvScript from '../../utils/loadOpenCvScript';

// Field self-test page (route: /diagnostics — deliberately unlinked; give
// the URL to a tester and ask for a screenshot). One screen answers the
// questions that each cost a debugging round-trip during the AR launch
// firefight: does this device have WebGL/WebXR, is the service worker
// healthy, does the OpenCV runtime initialise, is analytics flowing, can it
// reach the network. Passive checks run automatically; anything heavy or
// permission-prompting (camera, the ~10 MB OpenCV load, test events) is
// button-triggered.

type CheckState = { label: string; value: string; ok: boolean | null };

const check = (
  label: string,
  value: string,
  ok: boolean | null = true
): CheckState => ({ label, value, ok });

async function runPassiveChecks(): Promise<CheckState[]> {
  const results: CheckState[] = [];

  results.push(check('User agent', navigator.userAgent, null));
  results.push(
    check(
      'Viewport',
      `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}`,
      null
    )
  );

  // The id compiled into this bundle, next to the deployed one below: if
  // they differ, an update is on its way (or waiting behind the banner).
  results.push(check('This build', __APP_BUILD_ID__, null));

  try {
    // Bounded: on a captive portal or dead network a hanging fetch would
    // otherwise leave the whole page on "running checks…" forever.
    const res = await fetch('/version.json', {
      cache: 'no-store',
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) {
      results.push(
        check('Deployed build', `version.json HTTP ${res.status}`, false)
      );
    } else {
      const { buildId } = (await res.json()) as { buildId?: string };
      results.push(check('Deployed build', buildId ?? 'unknown', !!buildId));
    }
  } catch (error) {
    results.push(
      check(
        'Deployed build',
        error instanceof DOMException && error.name === 'TimeoutError'
          ? 'version.json timed out (5s)'
          : 'version.json unreachable',
        false
      )
    );
  }

  results.push(
    check('Online', String(navigator.onLine), navigator.onLine !== false)
  );

  try {
    window.localStorage.setItem('mt-diag', '1');
    window.localStorage.removeItem('mt-diag');
    results.push(check('localStorage', 'writable', true));
  } catch {
    results.push(check('localStorage', 'UNAVAILABLE', false));
  }

  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const cacheNames = (await caches.keys()).join(', ') || 'none';
    results.push(
      check(
        'Service worker',
        reg
          ? `${reg.active ? 'active' : 'not active'}${
              navigator.serviceWorker.controller ? ', controlling' : ''
            } | caches: ${cacheNames}`
          : 'not registered',
        !!reg?.active
      )
    );
  } catch {
    results.push(check('Service worker', 'error', false));
  }

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (gl) {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      const renderer = dbg
        ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))
        : 'renderer masked';
      results.push(check('WebGL', renderer, true));
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    } else {
      results.push(check('WebGL', 'NO CONTEXT', false));
    }
  } catch {
    results.push(check('WebGL', 'error', false));
  }

  try {
    const supported = navigator.xr
      ? await navigator.xr.isSessionSupported('immersive-ar')
      : false;
    results.push(
      check(
        'WebXR immersive-ar',
        navigator.xr ? String(supported) : 'no navigator.xr',
        supported
      )
    );
  } catch {
    results.push(check('WebXR immersive-ar', 'error', false));
  }

  try {
    const cams = (await navigator.mediaDevices.enumerateDevices()).filter(
      (d) => d.kind === 'videoinput'
    ).length;
    results.push(check('Cameras (enumerated)', String(cams), cams > 0));
  } catch {
    results.push(check('Cameras (enumerated)', 'error', false));
  }

  const phLoaded = posthog.__loaded;
  results.push(
    check(
      'PostHog',
      phLoaded
        ? `loaded, api_host=${posthog.config.api_host}${
            posthog.has_opted_out_capturing() ? ', OPTED OUT' : ''
          }`
        : 'not initialised',
      !!phLoaded
    )
  );

  return results;
}

// ── AR sensors and video (TrailSLAM field tests) ────────────────────────
//
// What the TrailSLAM engine depends on and phones differ in: how fast the
// motion sensors report, whether linear acceleration exists at all (without
// it the scale estimator derives it from gravity), which camera frame
// timestamps the browser exposes (captureTime lets the engine skip
// measuring the video latency), and what test flags are in force. The
// sensor and camera checks prompt on iOS, so they are buttons.

const AR_FLAG_KEYS = {
  arBackend: 'mt-ar-backend',
  arLatency: 'mt-ar-latency',
  arRecord: 'mt-ar-record',
  arDebug: 'mt-ar-debug',
  // On/off engine features (recorder/arFlags.ts): only an explicit off is
  // stored, so a missing key means on.
  arSync: 'mt-ar-sync',
  arGyro: 'mt-ar-gyro',
  arCoast: 'mt-ar-coast',
} as const;

const FOV_STORAGE_KEY = 'mt.ar.rearCameraFov.v1';

const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

/** The ?ar… flags in force: this URL first, then what the tab kept. */
const arOverridesRow = (): CheckState => {
  const params = new URLSearchParams(window.location.search);
  const value = (name: keyof typeof AR_FLAG_KEYS, fallback: string) =>
    params.get(name) ?? readSession(AR_FLAG_KEYS[name]) ?? fallback;
  const onOff = (name: 'arSync' | 'arGyro' | 'arCoast') =>
    `${name}=${value(name, '1') === '0' ? 'off' : 'on'}`;
  const parts = [
    `arBackend=${value('arBackend', 'auto')}`,
    `arLatency=${value('arLatency', 'auto')}`,
    `arRecord=${value('arRecord', '0')}`,
    `arDebug=${value('arDebug', '0')}`,
    onOff('arSync'),
    onOff('arGyro'),
    onOff('arCoast'),
  ];
  const fov = params.get('arFov');
  if (fov) parts.push(`arFov=${fov}`);
  return check('AR test flags', parts.join(' · '), null);
};

const storedFovRow = (): CheckState => {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(FOV_STORAGE_KEY);
  } catch {
    raw = null;
  }
  if (!raw)
    return check(
      'Stored camera FOV',
      'none yet: TrailSLAM uses 62° until two sessions agree',
      null
    );
  try {
    const { fovDeg, weight, sessions } = JSON.parse(raw) as {
      fovDeg?: number;
      weight?: number;
      sessions?: number;
    };
    if (typeof fovDeg !== 'number') throw new Error('no fovDeg');
    const agreed = sessions ?? 1;
    return check(
      'Stored camera FOV',
      `${fovDeg.toFixed(1)}° from ${agreed} session${agreed === 1 ? '' : 's'}, weight ${weight ?? '?'}` +
        (agreed >= 2 ? ', in use' : ', not trusted yet'),
      null
    );
  } catch {
    return check('Stored camera FOV', `unreadable: ${raw.slice(0, 60)}`, false);
  }
};

type PermissionRequester = { requestPermission?: () => Promise<string> };

/** iOS asks; everywhere else the sensors are simply on. */
const requestSensorPermission = (ctor: unknown): Promise<string> => {
  const request = (ctor as PermissionRequester | undefined)?.requestPermission;
  if (typeof request !== 'function') return Promise.resolve('not required');
  try {
    return request
      .call(ctor)
      .catch((error: unknown) => `refused: ${String(error)}`);
  } catch (error) {
    return Promise.resolve(`refused: ${String(error)}`);
  }
};

interface SensorStats {
  orientation: number;
  orientationWithAngles: number;
  absolute: boolean;
  motion: number;
  linearNull: number;
  gravity: number;
  rotationRate: number;
  intervals: number[];
  /** event.timeStamp − performance.now() per event (ms). */
  clockOffsets: number[];
}

const measureSensors = (durationMs: number): Promise<SensorStats> =>
  new Promise((resolve) => {
    const stats: SensorStats = {
      orientation: 0,
      orientationWithAngles: 0,
      absolute: false,
      motion: 0,
      linearNull: 0,
      gravity: 0,
      rotationRate: 0,
      intervals: [],
      clockOffsets: [],
    };
    const onOrientation = (event: DeviceOrientationEvent) => {
      stats.orientation++;
      if (event.alpha !== null && event.beta !== null && event.gamma !== null)
        stats.orientationWithAngles++;
      if (event.absolute) stats.absolute = true;
      stats.clockOffsets.push(event.timeStamp - performance.now());
    };
    const onMotion = (event: DeviceMotionEvent) => {
      stats.motion++;
      const a = event.acceleration;
      if (!a || a.x === null || a.y === null || a.z === null)
        stats.linearNull++;
      const g = event.accelerationIncludingGravity;
      if (g && g.x !== null && g.y !== null && g.z !== null) stats.gravity++;
      const r = event.rotationRate;
      if (r && r.alpha !== null && r.beta !== null && r.gamma !== null)
        stats.rotationRate++;
      if (Number.isFinite(event.interval)) stats.intervals.push(event.interval);
    };
    window.addEventListener('deviceorientation', onOrientation);
    window.addEventListener('devicemotion', onMotion);
    window.setTimeout(() => {
      window.removeEventListener('deviceorientation', onOrientation);
      window.removeEventListener('devicemotion', onMotion);
      resolve(stats);
    }, durationMs);
  });

const medianOf = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};

const sensorRows = (
  orientationPermission: string,
  motionPermission: string,
  stats: SensorStats,
  seconds: number
): CheckState[] => {
  const permissionOk = (p: string) => p === 'granted' || p === 'not required';
  const orientationHz = stats.orientationWithAngles / seconds;
  const motionHz = stats.motion / seconds;
  const offset = medianOf(stats.clockOffsets);
  const interval = medianOf(stats.intervals);
  return [
    check(
      'Sensor permission',
      `orientation ${orientationPermission}, motion ${motionPermission}`,
      permissionOk(orientationPermission) && permissionOk(motionPermission)
    ),
    check(
      'Orientation events',
      stats.orientation > 0 && stats.orientationWithAngles === 0
        ? `${stats.orientation} events, all without angles (no gyroscope?)`
        : `${orientationHz.toFixed(0)} Hz over ${seconds} s${stats.absolute ? ', absolute (compass)' : ''}`,
      orientationHz >= 30
    ),
    check(
      'Motion events',
      `${motionHz.toFixed(0)} Hz${interval !== null ? `, interval ${interval.toFixed(1)} ms` : ''}`,
      motionHz >= 30
    ),
    check(
      'Linear acceleration',
      stats.motion === 0
        ? 'no motion events'
        : stats.linearNull === stats.motion
          ? 'NULL on this device: scale uses acceleration derived from gravity'
          : stats.linearNull > 0
            ? `present, but null in ${stats.linearNull} of ${stats.motion} events`
            : 'present (event.acceleration)',
      stats.motion > 0 && stats.linearNull === 0
    ),
    check(
      'Acceleration with gravity',
      stats.gravity > 0 ? 'present' : 'missing',
      stats.gravity > 0
    ),
    check(
      'Rotation rate',
      stats.rotationRate > 0 ? 'present' : 'missing',
      stats.rotationRate > 0
    ),
    check(
      'Sensor timestamps',
      offset === null
        ? 'no events'
        : Math.abs(offset) < 1000
          ? `on the page clock (${offset.toFixed(0)} ms behind)`
          : `another clock (off by ${offset.toFixed(0)} ms): handler time is used`,
      offset !== null && Math.abs(offset) < 1000
    ),
  ];
};

type FrameMetadata = VideoFrameCallbackMetadata & {
  captureTime?: number;
  receiveTime?: number;
  processingDuration?: number;
};

const METADATA_FIELDS = [
  'mediaTime',
  'presentationTime',
  'expectedDisplayTime',
  'captureTime',
  'receiveTime',
  'processingDuration',
  'presentedFrames',
] as const;

/**
 * Opens the rear camera the way the AR pages do and watches its frames for
 * two seconds: whether requestVideoFrameCallback runs, and which timing
 * fields each frame carries.
 */
const probeCameraFrames = async (): Promise<CheckState[]> => {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    audio: false,
  });
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  // In the document (invisibly): some engines only paint frames, and so only
  // run frame callbacks, for a video that is attached.
  Object.assign(video.style, {
    position: 'fixed',
    width: '2px',
    height: '2px',
    opacity: '0',
    pointerEvents: 'none',
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(video);
  try {
    video.srcObject = stream;
    await video.play();
    const track = stream.getVideoTracks()[0];
    const settings = track?.getSettings() ?? {};
    const rows: CheckState[] = [
      check(
        'Camera stream',
        `${video.videoWidth}x${video.videoHeight}` +
          `${settings.frameRate ? ` @ ${Math.round(settings.frameRate)} fps` : ''}` +
          ` (${track?.label || 'unlabelled'})`,
        video.videoWidth > 0
      ),
    ];
    if (typeof video.requestVideoFrameCallback !== 'function') {
      rows.push(
        check(
          'requestVideoFrameCallback',
          'NOT SUPPORTED: frames are paced by a timer',
          false
        )
      );
      return rows;
    }
    const seen = new Set<string>();
    const latencies: number[] = [];
    let callbacks = 0;
    const started = performance.now();
    await new Promise<void>((resolve) => {
      const stopAt = started + 2000;
      const onFrame = (now: number, metadata: VideoFrameCallbackMetadata) => {
        callbacks++;
        const m = metadata as FrameMetadata;
        for (const field of METADATA_FIELDS)
          if (typeof m[field] === 'number') seen.add(field);
        if (
          typeof m.captureTime === 'number' &&
          typeof m.expectedDisplayTime === 'number'
        )
          latencies.push(m.expectedDisplayTime - m.captureTime);
        if (now < stopAt) video.requestVideoFrameCallback(onFrame);
        else resolve();
      };
      video.requestVideoFrameCallback(onFrame);
      // A callback chain that never starts must not hang the page.
      window.setTimeout(resolve, 3000);
    });
    const seconds = (performance.now() - started) / 1000;
    const latency = medianOf(latencies);
    rows.push(
      check(
        'requestVideoFrameCallback',
        `${callbacks} frames in ${seconds.toFixed(1)} s (${(callbacks / seconds).toFixed(0)} fps)`,
        callbacks >= 10
      ),
      check(
        'Frame timing fields',
        seen.size ? [...seen].join(', ') : 'none',
        null
      ),
      check(
        'Frame captureTime',
        seen.has('captureTime')
          ? `present: capture to display ${latency === null ? 'n/a' : `${latency.toFixed(0)} ms`}`
          : 'absent: the engine measures video latency from gyro vs SLAM',
        seen.has('captureTime')
      )
    );
    return rows;
  } finally {
    stream.getTracks().forEach((t) => t.stop());
    video.remove();
  }
};

const AR_TEST_LINKS = [
  {
    href: '/page-test/creaturePhoto/play?arDebug=1',
    label: 'Creature Photo with the AR debug overlay',
  },
  {
    href: '/page-test/creaturePhoto/play?arRecord=1',
    label: 'Creature Photo, recording hands-free',
  },
  {
    href: '/ar-test?arDebug=1&arBackend=trailslam',
    label: '/ar-test harness with TrailSLAM forced',
  },
];

// Inline styles are DELIBERATE throughout this page (same rationale as the
// AR debug overlay): a diagnostic surface has to render correctly in exactly
// the broken environments it exists to debug — including one where the CSS
// bundle or Tailwind utilities failed to load.
const Row = ({ item }: { item: CheckState }) => (
  <div style={{ marginBottom: 6 }}>
    <span
      style={{
        color: item.ok === null ? '#9ca3af' : item.ok ? '#4ade80' : '#f87171',
      }}
    >
      {item.ok === null ? '·' : item.ok ? '✓' : '✗'} {item.label}:
    </span>{' '}
    <span style={{ wordBreak: 'break-word' }}>{item.value}</span>
  </div>
);

export default function Diagnostics() {
  const [checks, setChecks] = useState<CheckState[]>([]);
  const [active, setActive] = useState<CheckState[]>([]);
  // In-flight guards: loadOpenCvScript documents that single-flight is the
  // caller's job, and a double-tapped camera probe would race two
  // getUserMedia acquisitions.
  const [opencvPending, setOpencvPending] = useState(false);
  const [cameraPending, setCameraPending] = useState(false);
  const [arRows, setArRows] = useState<CheckState[]>([]);
  const [arFlagsRow, setArFlagsRow] = useState<CheckState>(arOverridesRow);
  const [sensorsPending, setSensorsPending] = useState(false);
  const [framesPending, setFramesPending] = useState(false);

  useEffect(() => {
    runPassiveChecks()
      .then(setChecks)
      .catch((error: unknown) => {
        setChecks([
          check('Diagnostics', `checks crashed: ${String(error)}`, false),
        ]);
      });
  }, []);

  const pushActive = (item: CheckState) =>
    setActive((prev) => [...prev.filter((c) => c.label !== item.label), item]);

  const testOpenCv = async () => {
    if (opencvPending) return;
    setOpencvPending(true);
    pushActive(check('OpenCV', 'loading ~10 MB…', null));
    const started = performance.now();
    try {
      const cv = (await loadOpenCvScript()) as { Mat?: unknown };
      const ms = Math.round(performance.now() - started);
      pushActive(
        check('OpenCV', `ready in ${ms}ms (Mat=${typeof cv.Mat})`, true)
      );
    } catch (error) {
      pushActive(check('OpenCV', String(error), false));
    } finally {
      setOpencvPending(false);
    }
  };

  const testCamera = async () => {
    if (cameraPending) return;
    setCameraPending(true);
    pushActive(check('Camera access', 'requesting…', null));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      const label = stream.getVideoTracks()[0]?.label || 'unlabelled track';
      stream.getTracks().forEach((t) => t.stop());
      pushActive(check('Camera access', label, true));
    } catch (error) {
      pushActive(check('Camera access', String(error), false));
    } finally {
      setCameraPending(false);
    }
  };

  const testEvents = () => {
    // One caught error through the library contract and one plain capture —
    // both should appear in PostHog within a minute if the pipeline works.
    reportCaughtError({
      source: 'Diagnostics',
      kind: 'self-test',
      message: 'diagnostics test event',
    });
    posthog.capture('diagnosticsSelfTest');
    pushActive(
      check(
        'Test events',
        'dispatched clientDiagnostic + diagnosticsSelfTest',
        true
      )
    );
  };

  const pushArRows = (rows: CheckState[]) =>
    setArRows((prev) => [
      ...prev.filter((c) => !rows.some((r) => r.label === c.label)),
      ...rows,
    ]);

  const testSensors = () => {
    if (sensorsPending) return;
    setSensorsPending(true);
    pushArRows([check('Sensor permission', 'requesting…', null)]);
    // Both requests start inside the tap: iOS needs the gesture for each.
    const orientationPermission = requestSensorPermission(
      typeof DeviceOrientationEvent === 'undefined'
        ? undefined
        : DeviceOrientationEvent
    );
    const motionPermission = requestSensorPermission(
      typeof DeviceMotionEvent === 'undefined' ? undefined : DeviceMotionEvent
    );
    void Promise.all([orientationPermission, motionPermission])
      .then(async ([orientation, motion]) => {
        pushArRows([
          check(
            'Sensor permission',
            `${orientation} / ${motion}; measuring 2 s…`,
            null
          ),
        ]);
        const stats = await measureSensors(2000);
        pushArRows(sensorRows(orientation, motion, stats, 2));
      })
      .catch((error: unknown) =>
        pushArRows([check('Sensor permission', String(error), false)])
      )
      .finally(() => setSensorsPending(false));
  };

  const testFrames = async () => {
    if (framesPending) return;
    setFramesPending(true);
    pushArRows([check('Camera stream', 'opening the rear camera…', null)]);
    try {
      pushArRows(await probeCameraFrames());
    } catch (error) {
      pushArRows([check('Camera stream', String(error), false)]);
    } finally {
      setFramesPending(false);
    }
  };

  const clearArFlags = () => {
    for (const key of Object.values(AR_FLAG_KEYS)) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // Nothing stored to clear.
      }
    }
    setArFlagsRow(arOverridesRow());
  };

  // WebXR support decides the backend: say what it means for TrailSLAM.
  const xrCheck = checks.find((c) => c.label === 'WebXR immersive-ar');
  const backendRow = xrCheck
    ? check(
        'AR backend on this phone',
        xrCheck.ok
          ? 'WebXR for world pages; TrailSLAM on photo pages or with ?arBackend=trailslam'
          : 'TrailSLAM (no WebXR immersive-ar)',
        null
      )
    : null;

  const button: React.CSSProperties = {
    background: '#4338ca',
    color: 'white',
    borderRadius: 6,
    padding: '8px 14px',
    marginRight: 8,
    marginBottom: 8,
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0f172a',
        color: '#e2e8f0',
        fontFamily: 'monospace',
        fontSize: 13,
        padding: 16,
      }}
    >
      <h1 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
        Magical Trails device diagnostics
      </h1>
      {checks.length === 0 ? <div>running checks…</div> : null}
      {checks.map((c) => (
        <Row key={c.label} item={c} />
      ))}
      <div style={{ margin: '14px 0 6px', color: '#9ca3af' }}>
        On-demand (may prompt or download):
      </div>
      <div>
        <button
          type="button"
          style={{ ...button, opacity: opencvPending ? 0.5 : 1 }}
          disabled={opencvPending}
          onClick={() => void testOpenCv()}
        >
          Test OpenCV
        </button>
        <button
          type="button"
          style={{ ...button, opacity: cameraPending ? 0.5 : 1 }}
          disabled={cameraPending}
          onClick={() => void testCamera()}
        >
          Test camera
        </button>
        <button type="button" style={button} onClick={testEvents}>
          Send test events
        </button>
      </div>
      {active.map((c) => (
        <Row key={c.label} item={c} />
      ))}
      <div style={{ margin: '18px 0 6px', color: '#9ca3af' }}>
        AR sensors and video (TrailSLAM):
      </div>
      <Row item={arFlagsRow} />
      <Row item={storedFovRow()} />
      {backendRow ? <Row item={backendRow} /> : null}
      <div style={{ marginTop: 8 }}>
        <button
          type="button"
          style={{ ...button, opacity: sensorsPending ? 0.5 : 1 }}
          disabled={sensorsPending}
          onClick={testSensors}
        >
          Test motion sensors
        </button>
        <button
          type="button"
          style={{ ...button, opacity: framesPending ? 0.5 : 1 }}
          disabled={framesPending}
          onClick={() => void testFrames()}
        >
          Test camera frames
        </button>
        <button type="button" style={button} onClick={clearArFlags}>
          Clear AR flags
        </button>
      </div>
      {arRows.map((c) => (
        <Row key={c.label} item={c} />
      ))}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* Full page loads, not router links: the flags are read at start-up. */}
        {AR_TEST_LINKS.map((link) => (
          <a key={link.href} href={link.href} style={{ color: '#93c5fd' }}>
            {link.href} ({link.label})
          </a>
        ))}
      </div>
      <div style={{ margin: '18px 0 6px', color: '#9ca3af' }}>
        Other engineering pages:
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Link to="/lab" style={{ color: '#93c5fd' }}>
          /lab — vision capability tests (object scanner, person scanner)
        </Link>
        <Link to="/ar-test" style={{ color: '#93c5fd' }}>
          /ar-test — AR isolation harness
        </Link>
        <Link to="/page-test" style={{ color: '#93c5fd' }}>
          /page-test — open any page type with adjustable settings
        </Link>
      </div>
    </div>
  );
}
